
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, X, Users, Loader } from 'lucide-react';
import { User } from './types';
import { createGroupConversation } from './services/chatService';
import { searchUsersByName, UserPublicProfile } from './services/userService';

export const CreateGroupView = ({
  user,
  onBack,
  onGroupCreated,
  t
}: {
  user: User;
  onBack: () => void;
  onGroupCreated: (conversationId: string, otherUserId: string) => void;
  t?: any;
}) => {
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<UserPublicProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserPublicProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Debounced search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchUsersByName(searchTerm, user.id);
        // Filter out already selected members
        const filtered = results.filter(
          r => !selectedMembers.some(m => m.uid === r.uid)
        );
        setSearchResults(filtered);
      } catch (err) {
        console.error('[CREATE_GROUP] Search error:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedMembers, user.id]);

  const handleAddMember = (member: UserPublicProfile) => {
    if (!selectedMembers.some(m => m.uid === member.uid)) {
      setSelectedMembers([...selectedMembers, member]);
      setSearchTerm('');
      setSearchResults([]);
    }
  };

  const handleRemoveMember = (uid: string) => {
    setSelectedMembers(selectedMembers.filter(m => m.uid !== uid));
  };

  const handleCreate = async () => {
    // Prevent double-submit
    if (creating) return;
    
    setError('');
    
    if (!groupName.trim()) {
      setError(t?.chat_group_name_required || 'Il nome del gruppo è obbligatorio');
      return;
    }

    if (selectedMembers.length === 0) {
      setError(t?.chat_group_add_member_error || 'Aggiungi almeno un membro al gruppo');
      return;
    }

    setCreating(true);
    try {
      const memberUids = selectedMembers.map(m => m.uid);
      const conversationId = await createGroupConversation(
        user.id,
        groupName.trim(),
        memberUids,
        description.trim() || undefined,
        undefined // photoURL - not implemented in MVP
      );
      
      // Navigate to the group chat (use first member as otherUserId for routing compatibility)
      onGroupCreated(conversationId, memberUids[0]);
    } catch (err: unknown) {
      console.error('[CREATE_GROUP] Error creating group:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(t?.chat_error_create_group || `Error creating group: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-slate-800">
        <div className="flex items-center gap-4 p-4 md:p-6">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <Users className="text-cyan-400" size={24} />
            <h1 className="text-2xl font-black text-white uppercase tracking-tight">{t?.chat_group_create || 'Nuovo Gruppo'}</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 max-w-2xl mx-auto w-full">
        {error && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-2xl border border-red-500/20 text-sm">
            {error}
          </div>
        )}

        {/* Group Name */}
        <div>
          <label className="block text-sm font-black text-slate-400 uppercase tracking-widest mb-2">
            {t?.chat_group_name || 'Nome Gruppo'} *
          </label>
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder={t?.chat_group_name_placeholder || 'Es: Squadra Palestra'}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
            maxLength={50}
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-black text-slate-400 uppercase tracking-widest mb-2">
            {t?.chat_group_description || 'Descrizione (opzionale)'}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t?.chat_group_description_placeholder || 'Aggiungi una descrizione per il gruppo...'}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors resize-none"
            rows={3}
            maxLength={200}
          />
        </div>

        {/* Member Search */}
        <div>
          <label className="block text-sm font-black text-slate-400 uppercase tracking-widest mb-2">
            {t?.chat_group_add_members || 'Add Members'}
          </label>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t?.chat_search_users_placeholder || t?.chat_group_search_users || "Cerca utenti per nome..."}
              className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
            />
            {searching && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <Loader className="animate-spin text-slate-500" size={18} />
              </div>
            )}
          </div>

          {/* Search Results */}
          {searchTerm.trim() && searchResults.length > 0 && (
            <div className="mt-2 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              {searchResults.map((result) => (
                <button
                  key={result.uid}
                  onClick={() => handleAddMember(result)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-slate-800 transition-colors text-left"
                >
                  <img
                    src={result.photoURL}
                    alt={result.displayName}
                    className="w-10 h-10 rounded-xl object-cover"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-white">{result.displayName}</p>
                    {result.username && (
                      <p className="text-xs text-slate-500">@{result.username}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {searchTerm.trim() && !searching && searchResults.length === 0 && (
            <div className="mt-2 text-center text-slate-500 text-sm py-4">
              {t?.chat_group_no_users || 'Nessun utente trovato'}
            </div>
          )}
        </div>

        {/* Selected Members */}
        {selectedMembers.length > 0 && (
          <div>
            <label className="block text-sm font-black text-slate-400 uppercase tracking-widest mb-2">
              {t?.chat_group_selected_members || 'Selected Members'} ({selectedMembers.length})
            </label>
            <div className="flex flex-wrap gap-2">
              {selectedMembers.map((member) => (
                <div
                  key={member.uid}
                  className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2"
                >
                  <img
                    src={member.photoURL}
                    alt={member.displayName}
                    className="w-6 h-6 rounded-lg object-cover"
                  />
                  <span className="text-xs font-bold text-white">{member.displayName}</span>
                  <button
                    onClick={() => handleRemoveMember(member.uid)}
                    className="p-1 hover:bg-slate-700 rounded transition-colors"
                  >
                    <X size={14} className="text-slate-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Button */}
        <button
          onClick={handleCreate}
          disabled={creating || !groupName.trim() || selectedMembers.length === 0}
          className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-800 disabled:text-slate-600 text-white font-black py-4 rounded-xl transition-all disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
            {creating ? (
              <>
                <Loader className="animate-spin" size={20} />
                <span>{t?.chat_group_creating || 'Creazione...'}</span>
              </>
            ) : (
              <span>{t?.chat_group_create_button || 'Crea Gruppo'}</span>
            )}
        </button>
      </div>
    </div>
  );
};

